import { sizeOptions } from "components/grid/page-size";
import { defaultSortOptions } from "components/grid/sort-options";
import ResourcesList, { ResourcesListProps } from "components/views/resources/list";
import { excludeDraftFilter, imageFragment } from "lib/sanity/queries";
import { getClient } from "lib/sanity/server";
import { FC } from "react";

import Page from "../../components/layout/page";

type ResourcesPageProps = ResourcesListProps & {
  seoTitle: string;
  seoDescription: string;
};
const ResourcesPage: FC<ResourcesPageProps> = ({ seoTitle, seoDescription, ...rest }: ResourcesPageProps) => {
  return (
    <Page title={seoTitle} description={seoDescription}>
      <ResourcesList {...rest} />
    </Page>
  );
};

export default ResourcesPage;

const filterKeys = [
  { displayName: "topic", key: "topics", multiple: true },
  { displayName: "type", key: "type", multiple: false },
];

export async function getServerSideProps(ctx) {
  const client = getClient(ctx.preview);
  const title = "Welcome to the 'Flo";
  const currentPage = ctx.query.page ? Number(ctx.query.page as string) : 1;
  const sortBy = (ctx.query.sortBy as string) || defaultSortOptions[0].value;

  const subtitle = "Browse resources and information about Enerflo, the Solar Industry, and more.";
  let pageSize = Number((ctx.query.pageSize as string) || sizeOptions[0]);

  // If user is trying to view a page size that isn't a valid option, just use first size option
  if (pageSize && !sizeOptions.includes(pageSize.toString())) {
    pageSize = Number(sizeOptions[0]);
  }

  let filterData = null;

  try {
    filterData = await client.fetch(
      `
      {
        "topic": *[_type == "resourceTopic"] | order(label asc),
        "type": *[_type == "resourceType"] | order(label asc)
      }
      `
    );
  } catch (e) {
    console.error("Unable to fetch filter options", e);
  }

  const filterOptions = Object.entries(filterData || {}).map(([key, options]) => ({ name: key, options }));
  const queryFilters = ['_type == "resource"'];
  const filters = {};

  if (!ctx.preview) {
    queryFilters.push(excludeDraftFilter);
  }

  filterKeys.forEach(filterKey => {
    if (ctx.query[filterKey.displayName]) {
      filters[filterKey.displayName] = ctx.query[filterKey.displayName];

      if (filterKey.multiple) {
        queryFilters.push(`"${ctx.query[filterKey.displayName]}" in ${filterKey.key}[]->value`);
      } else {
        queryFilters.push(`${filterKey.key}->value == "${ctx.query[filterKey.displayName]}"`);
      }
    }
  });

  const filterString = queryFilters.join(" && ");
  let totalCount = 0;

  try {
    totalCount = await client.fetch(
      `
      count(*[${filterString}])
    `
    );
  } catch (e) {
    console.error("Unable to fetch items count", e);
  }

  const orderByString = `order(${sortBy})`;
  const pagesCount = Math.ceil(totalCount / pageSize);
  const pageStart = (Math.max(1, Math.min(currentPage || 1, pagesCount)) - 1) * pageSize;
  const pageEnd = Math.min(pageStart + pageSize, totalCount);
  const paginationString = `[${pageStart}...${pageEnd}]`;
  let items = [];

  try {
    items = await client.fetch(
      `
      *[${filterString}] | ${orderByString} ${paginationString} {
        _id,
        postedDate,
        "slug": slug.current,
        image {
          ${imageFragment}
        },
        title,
        type->
      }
    `
    );
  } catch (e) {
    console.error("Unable to fetch items", e);
  }

  return {
    props: {
      items,
      title,
      subtitle,
      filters,
      filterOptions,
      currentPage,
      pageSize,
      sortBy,
      totalCount,
    },
  };
}
